import { useCallback, useEffect, useState } from "react";
import type { Replay } from "../types.js";
import { parseReplayFile } from "../replayLoader.js";
import { loadManifest } from "../manifest.js";

export interface LibraryScreenProps {
  replays: Replay[];
  onLoaded: (r: Replay[]) => void;
  onOpen: (r: Replay) => void;
}

export function replayMatchesFilter(replay: Replay, filter: string): boolean {
  if (!filter) return true;
  const needle = filter.toLowerCase();
  return (
    replay.envelope.game_id.toLowerCase().includes(needle) ||
    (replay.envelope.result.winnerId ?? "").toLowerCase().includes(needle) ||
    replay.envelope.agent_names_by_seat.some((a) =>
      a.toLowerCase().includes(needle),
    ) ||
    (replay.envelope.flags ?? []).some((f) => f.toLowerCase().includes(needle))
  );
}

export function LibraryScreen({ replays, onLoaded, onOpen }: LibraryScreenProps) {
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [manifestState, setManifestState] = useState<
    "checking" | "loaded" | "absent" | "error"
  >("checking");

  useEffect(() => {
    if (replays.length > 0) {
      setManifestState("loaded");
      return;
    }
    const ctl = new AbortController();
    void loadManifest({ signal: ctl.signal }).then((res) => {
      if (ctl.signal.aborted) return;
      if (!res) {
        setManifestState("absent");
        return;
      }
      if (res.errors.length > 0) {
        setError(res.errors.join("; "));
      }
      if (res.replays.length > 0) {
        onLoaded(res.replays);
        setManifestState("loaded");
      } else {
        setManifestState(res.errors.length > 0 ? "error" : "absent");
      }
    });
    return () => ctl.abort();
    // Intentional: only run on mount / when we drain to empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiles = useCallback(
    async (files: FileList | null | undefined) => {
      if (!files) return;
      setError(null);
      const parsed: Replay[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files.item(i);
        if (!f) continue;
        if (!f.name.endsWith(".jsonl")) continue;
        try {
          parsed.push(await parseReplayFile(f));
        } catch (err) {
          setError(String(err));
        }
      }
      onLoaded([...replays, ...parsed]);
    },
    [onLoaded, replays],
  );

  const rows = replays
    .filter((r) => replayMatchesFilter(r, filter))
    .sort((a, b) => a.envelope.seed - b.envelope.seed);

  return (
    <div className="library">
      <header className="library__header">
        <h1>Buried Treasure — Replay Library</h1>
        <p className="library__hint">
          {manifestState === "checking" && "Looking for /replays/manifest.json…"}
          {manifestState === "loaded" &&
            `${replays.length} replay${replays.length === 1 ? "" : "s"} loaded.`}
          {manifestState === "absent" && (
            <>
              No <code>/replays/manifest.json</code> found. Drop{" "}
              <code>.jsonl</code> files exported by <code>@bobt/sim</code> to
              load them manually.
            </>
          )}
          {manifestState === "error" && "Manifest load failed."}
        </p>
      </header>
      <div
        className="library__dropzone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void handleFiles(e.dataTransfer?.files);
        }}
      >
        <label className="library__filepick">
          <input
            type="file"
            multiple
            accept=".jsonl,application/json"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <span>Drop replay files here or click to choose</span>
        </label>
      </div>
      {error && <div className="library__error">{error}</div>}
      <div className="library__filter">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by game_id, winner, agent, or flag…"
        />
        <span>
          {rows.length} / {replays.length} games
        </span>
      </div>
      <table className="library__table">
        <thead>
          <tr>
            <th>Game</th>
            <th>Seed</th>
            <th>Ruleset</th>
            <th>Seats</th>
            <th>Winner</th>
            <th>Status</th>
            <th>Turns</th>
            <th>Flags</th>
            <th>Warnings</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.filename}>
              <td>{r.envelope.game_id}</td>
              <td>{r.envelope.seed}</td>
              <td>
                <code>{r.envelope.ruleset_hash}</code>
              </td>
              <td>{r.envelope.agent_names_by_seat.join(" · ")}</td>
              <td>{r.envelope.result.winnerId ?? "—"}</td>
              <td>{r.envelope.result.status}</td>
              <td>{r.envelope.result.finalTurn ?? "—"}</td>
              <td className="library__flags">
                {(r.envelope.flags ?? []).map((f) => (
                  <span key={f} className="library__flag">
                    {f}
                  </span>
                ))}
              </td>
              <td>
                {r.warnings.length > 0 ? (
                  <span className="library__warning">{r.warnings.length}</span>
                ) : (
                  "—"
                )}
              </td>
              <td>
                <button onClick={() => onOpen(r)}>Watch</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
